import { SUBTYPE_OPTIONS } from '@cms/utils/constants';
import { mapLookupsToOptions } from '@cms/utils/helpers';
import { collectionsLookupsQuery } from '@cms/utils/queries';
import { useAppSelector } from '@cms/utils/store/hooks';
import { ISystemReport } from '@dl/declarations';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormData, initialValues, schema } from '../../utils/forms/report';
import { ReportsPath } from '../../utils/routes';
import { ControlledDropdown } from '../data/Dropdown';
import { ControlledAssetInput } from '../data/FileInput';
import { ControlledSystemCheckbox } from '../data/Checkbox';
import { ControlledSystemTextInput } from '../data/TextInput';
import { FormContainer } from './FormContainer';

const Form = () => {
    const { entity } = useAppSelector((state) => state.form)
    const { setValue, watch } = useFormContext<FormData>();
    const selected_subtype = watch('subtype');
    const highlighted = watch('highlighted', false);

    const { data: collections = [] } = collectionsLookupsQuery({
        subtype: selected_subtype || '',
        collection_type: 'Report'
    }, {
        enabled: !!selected_subtype
    })

    useEffect(() => {
        setValue('collections', ((entity as ISystemReport)?.collectionsObjects || []).map((item) => item.collection_ref))
    }, [entity])

    return (
        <div className='w-full flex flex-col items-stretch gap-4'>
            <ControlledDropdown
                description='Subtype'
                name='subtype'
                options={SUBTYPE_OPTIONS}
                label='Add a content type'
                valueKey='_id'
                inputSize='full'
                searchOptions={{
                    allow: false
                }}
                allowSingleDeselect={false}
            />

            <ControlledDropdown
                description='Collection'
                name='collections'
                options={mapLookupsToOptions(collections, 'name', () => 'Collections')}
                label='Add to collections'
                valueKey='_id'
                inputSize='full'
                searchOptions={{
                    allow: false
                }}
                allowMultiSelect
                disabled={!selected_subtype}
            />

            <ControlledSystemTextInput
                description='Title'
                name='name'
                inputSize='full'
            />

            <ControlledSystemTextInput
                description='Description'
                name='description'
                inputSize='full'
                multiline
            />

            <ControlledSystemTextInput
                description='Report URL'
                name='report_url'
                inputSize='full'
            />

            <ControlledSystemTextInput
                description='Story URL'
                name='story_url'
                inputSize='full'
            />

            <div className='w-full flex items-center gap-4'>
                <ControlledAssetInput
                    name="report_image"
                    mainAction={{
                        show: true,
                        actionText: "Report image"
                    }}
                    initialValue={(entity as ISystemReport)?.reportImageObject || null}
                />

                <ControlledAssetInput
                    name="client_image"
                    mainAction={{
                        show: true,
                        actionText: "Client image"
                    }}
                    initialValue={(entity as ISystemReport)?.clientImageObject || null}
                />
            </div>

            <ControlledSystemCheckbox
                name='highlighted'
                label='Highlighted'
            />

            {highlighted && (
                <>
                    <div className='w-full flex items-center gap-4'>
                        <ControlledAssetInput
                            name="highlight_report_cover"
                            mainAction={{
                                show: true,
                                actionText: "Report cover"
                            }}
                            initialValue={(entity as ISystemReport)?.highlightReportCoverObject || null}
                        />

                        <ControlledAssetInput
                            name="highlight_sponsor_logo"
                            mainAction={{
                                show: true,
                                actionText: "Sponsor mini logo"
                            }}
                            initialValue={(entity as ISystemReport)?.highlightSponsorLogoObject || null}
                        />

                        <ControlledAssetInput
                            name="highlight_sponsor_logo_dark"
                            mainAction={{
                                show: true,
                                actionText: "Sponsor mini logo (dark)"
                            }}
                            initialValue={(entity as ISystemReport)?.highlightSponsorLogoDarkObject || null}
                        />
                    </div>

                    <ControlledSystemTextInput
                        description='Section title'
                        name='highlight_section_title'
                        inputSize='full'
                    />

                    <ControlledSystemTextInput
                        description='Section content'
                        name='highlight_section_content'
                        inputSize='full'
                        multiline
                        rows={8}
                    />
                </>
            )}
        </div>
    )
}

export const ReportForm = () => {
    return (
        <FormContainer<FormData>
            title='Report'
            formSize='1/2'
            headerOptions={{
                show: false
            }}
            queryOptions={{
                enabled: true,
                basePath: ReportsPath,
            }}
            schema={schema}
            defaultValues={initialValues}
            deleteOptions={{
                show: true
            }}
            shouldRedirectAfterSubmit={false}
        >
            <Form />
        </FormContainer>
    )
}
